export const SLOT_MINUTES = 15;
export const TIME_ROW_HEIGHT = 13;
export const PIXELS_PER_HOUR = 96;
export const PIXELS_PER_MINUTE = PIXELS_PER_HOUR / 60;
export const PIXELS_PER_SLOT = PIXELS_PER_HOUR / (60 / SLOT_MINUTES);
export const CARD_PADDING = 2;
export const MIN_DURATION_MINUTES = SLOT_MINUTES;
// Отступ между колонками постов (соответствует gap в PostsHeader) — виден пунктирный разделитель
export const POST_COLUMN_GAP = 16;
export const DAY_MODE_CARD_WIDTH = 120;
// Ширина колонки поста считается так, будто на экране всегда REFERENCE_POST_COUNT постов —
// больше постов не сжимают колонку, а уходят в горизонтальный скролл
export const REFERENCE_POST_COUNT = 3;
export const TIME_COLUMN_WIDTH = 58;
export const CONTENT_GAP = 31;
export const DRAG_THRESHOLD_PX = 4;
export const SLOT_VERTICAL_PADDING = 4;
export const AUTO_SCROLL_EDGE_PX = 56;
export const AUTO_SCROLL_MAX_STEP = 18;

export const snapMinutes = (minutes: number, step = SLOT_MINUTES): number => {
  return Math.round(minutes / step) * step;
};

export const clamp = (value: number, min: number, max: number): number => {
  return Math.min(max, Math.max(min, value));
};

export const getDurationMinutes = (startIso: string, endIso: string): number => {
  const duration = (new Date(endIso).getTime() - new Date(startIso).getTime()) / 60000;
  return Math.max(MIN_DURATION_MINUTES, duration);
};

export const minutesFromGridStart = (date: Date, minHour: number): number => {
  return (date.getHours() - minHour) * 60 + date.getMinutes();
};

export const minutesFromMidnight = (date: Date): number => {
  return date.getHours() * 60 + date.getMinutes();
};

export const setMinutesFromMidnight = (date: Date, totalMinutes: number): Date => {
  const result = new Date(date);
  result.setHours(Math.floor(totalMinutes / 60), totalMinutes % 60, 0, 0);
  return result;
};

export const rangesOverlap = (
  startA: number,
  endA: number,
  startB: number,
  endB: number,
): boolean => {
  return startA < endB && startB < endA;
};

export type CalendarWorkingHours = {
  start: number;
  end: number;
  openMinutes: number;
  closeMinutes: number;
};

export const getWorkingMinutesBounds = (workingHours: CalendarWorkingHours) => ({
  startMinutes: workingHours.openMinutes,
  endMinutes: workingHours.closeMinutes,
});

export const isWithinWorkingHours = (
  startMinutesFromMidnight: number,
  endMinutesFromMidnight: number,
  workingHours: CalendarWorkingHours,
): boolean => {
  const { startMinutes, endMinutes } = getWorkingMinutesBounds(workingHours);
  return startMinutesFromMidnight >= startMinutes && endMinutesFromMidnight <= endMinutes;
};

export const getBookingLayout = (
  startTime: string,
  endTime: string,
  minHour: number,
): { top: number; height: number } => {
  const start = new Date(startTime);
  const duration = getDurationMinutes(startTime, endTime);
  const top = minutesFromGridStart(start, minHour) * PIXELS_PER_MINUTE + CARD_PADDING;
  const height = Math.max(
    PIXELS_PER_SLOT - CARD_PADDING * 2,
    duration * PIXELS_PER_MINUTE - CARD_PADDING * 2,
  );
  return { top, height };
};

export const formatSlotRange = (hour: number, minutes: number, duration = SLOT_MINUTES): string => {
  const startTotal = hour * 60 + minutes;
  const endTotal = startTotal + duration;
  const pad = (value: number) => value.toString().padStart(2, '0');
  const startLabel = `${pad(Math.floor(startTotal / 60))}:${pad(startTotal % 60)}`;
  const endLabel = `${pad(Math.floor(endTotal / 60))}:${pad(endTotal % 60)}`;
  return `${startLabel} – ${endLabel}`;
};

export const getPostIndexFromX = (
  x: number,
  postColumnWidth: number,
  postsCount: number,
): number => {
  if (postsCount <= 0) return 0;
  const columnStride = postColumnWidth + POST_COLUMN_GAP;
  return clamp(Math.floor(x / columnStride), 0, postsCount - 1);
};

const startOfLocalDay = (date: Date): number => {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  return result.getTime();
};

/** Следующий 15-минутный слот, не раньше текущего момента. */
export const getNextSlotMinutesFromMidnight = (now: Date): number => {
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  if (nowMinutes % SLOT_MINUTES === 0 && now.getSeconds() === 0 && now.getMilliseconds() === 0) {
    return nowMinutes;
  }
  return Math.ceil(nowMinutes / SLOT_MINUTES) * SLOT_MINUTES;
};

/**
 * Нижняя граница старта для drag на выбранную дату.
 * `null` — весь день уже в прошлом, любой слот недопустим.
 */
export const getMinBookableStartMinutes = (
  currentDate: Date,
  workStartMinutes: number,
  now = new Date(),
): number | null => {
  const currentDay = startOfLocalDay(currentDate);
  const today = startOfLocalDay(now);

  if (currentDay < today) return null;
  if (currentDay > today) return workStartMinutes;

  return Math.max(workStartMinutes, getNextSlotMinutesFromMidnight(now));
};

export const isStartInPast = (
  currentDate: Date | undefined,
  startMinutes: number,
  now = new Date(),
): boolean => {
  if (!currentDate) return false;
  return setMinutesFromMidnight(currentDate, startMinutes).getTime() < now.getTime();
};

export const parseCssSize = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

export type DayCardSize = 'full' | 'short' | 'mini';

/** Компактность карточки дня по высоте (15 мин ≈ 20px, 45 мин ≈ 68px, 60 мин ≈ 92px). */
export const getDayCardSize = (heightPx: number | null): DayCardSize => {
  if (heightPx == null) return 'full';
  if (heightPx <= 36) return 'mini';
  if (heightPx <= 80) return 'short';
  return 'full';
};
